import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import { BrandSummary_T } from "../../../types/Brand";
import { getDateValue, renderDateCell, renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

interface BrandCountBySourceTableProps {
  brand_id: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "country_name", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: ["country_name", "country_code"] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
  aggregation: {
    model: {
      total_poi_count_filtered: "sum",
      omni_poi_count: "sum",
      omni_open_poi_count: "sum",
      non_omni_poi_count: "sum",
      non_omni_open_poi_count: "sum",
    },
  },
};

const columns_def: GridColDef[] = [
  {
    field: "country_name",
    headerName: "Country",
    description:
      "A country where this brand has physical locations, derived from the distinct countries of places with a chain_id for this brand.",
    type: "string",
    width: 200,
  },
  {
    field: "country_code",
    headerName: "Country Code",
    description: "ISO 3166-1 alpha-2 for this country.",
    type: "string",
    width: 100,
  },
  {
    field: "total_poi_count_filtered",
    headerName: "Active Count",
    description:
      "The number of non-ignored, non-duplicate places associated with this brand's chain_id.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "omni_poi_count",
    headerName: "Omni POI Count",
    description:
      "The total number of places associated with this brand's chain_id that have an external_id for an Omniscraper, excluding ignored at places.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "omni_open_poi_count",
    headerName: "Open Omni POI Count",
    description:
      "The number of places associated with this brand's chain_id that have an external_id for an Omniscraper and a status of 'Open'.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "non_omni_poi_count",
    headerName: "Non-Omni POI Count",
    description:
      "The total number of places associated with this brand's chain_id that do not have an external_id for an Omniscraper.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "non_omni_open_poi_count",
    headerName: "Non-Omni Open POI Count",
    description:
      "The number of places associated with this brand's chain_id that do not have an external_id for an Omniscraper and have a status of 'Open'.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "query_last_executed",
    headerName: "Data Last Updated",
    description:
      "The date when this data was retrieved and aggregated from the places table in the production database.",
    type: "date",
    width: 200,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
];

const BrandCountBySourceTable: React.FC<BrandCountBySourceTableProps> = ({
  brand_id,
}) => {
  const [brandCountBySourceData, setBrandCountBySourceData] = useState<
    BrandSummary_T[]
  >([]);
  const [loading, setLoading] = useState(true);

  const { showError } = useError();
  const { apiRequest } = useApi();

  useEffect(() => {
    if (brand_id) {
      const fetchBrandCountBySourceData = async () => {
        try {
          const response = await apiRequest<BrandSummary_T[]>(
            `/brand_reports/brand_count_by_source/${brand_id}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setBrandCountBySourceData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch brand count by source for brand ID ${brand_id}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBrandCountBySourceData();
    }
  }, [brand_id]);

  return (
    <GaneshDataGrid
      loading={loading}
      data={brandCountBySourceData}
      columns={columns_def}
      gridId="brandCountBySourceDataGrid"
      defaultGridState={defaultGridState}
    />
  );
};

export default BrandCountBySourceTable;
